import * as React from "react";
import { cva, VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import { Dialog as DialogPrimitive } from "radix-ui";
import { cn } from "../lib/utils";
import {
  PortalProvider,
  PortalDetector,
  usePortalContext,
  composeRefs,
  detectContainerFrom
} from "./portal-context";

const dialogContentVariants = cva(
  "flex flex-col fixed outline-0 z-50 border border-border bg-background p-6 shadow-lg shadow-black/5 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg",
  {
    variants: {
      variant: {
        default:
          "left-[50%] top-[50%] max-w-lg translate-x-[-50%] translate-y-[-50%] w-full",
        fullscreen: "inset-5"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);

function Dialog({
  children,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return (
    <PortalProvider>
      <DialogPrimitive.Root data-slot="dialog" {...props}>
        <PortalDetector />
        {children}
      </DialogPrimitive.Root>
    </PortalProvider>
  );
}

const DialogTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof DialogPrimitive.Trigger>
>(function DialogTrigger({ ...props }, forwardedRef) {
  const { setContainer } = usePortalContext("Dialog");
  const localRef = React.useRef<HTMLButtonElement>(null);

  // Detect on mount
  React.useLayoutEffect(() => {
    setContainer(detectContainerFrom(localRef.current));
  }, [setContainer]);

  // Also detect right before open (captures correct node if multiple dialogs)
  const handlePointerDown: React.PointerEventHandler<HTMLButtonElement> = (
    e
  ) => {
    setContainer(detectContainerFrom(e.currentTarget as HTMLElement));
    props.onPointerDown?.(e);
  };

  return (
    <DialogPrimitive.Trigger
      ref={composeRefs(forwardedRef, localRef)}
      data-slot="dialog-trigger"
      onPointerDown={handlePointerDown}
      {...props}
    />
  );
});

function DialogPortalWithShadow({
  children
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  const { container } = usePortalContext("Dialog");
  return (
    <DialogPrimitive.Portal
      // TS doesn't know ShadowRoot is acceptable here; cast is fine in practice.
      container={(container as unknown as HTMLElement) ?? undefined}
    >
      {children}
    </DialogPrimitive.Portal>
  );
}

// Keep a named export for compatibility
const DialogPortal = DialogPortalWithShadow;

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      data-slot="dialog-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-black/30 [backdrop-filter:blur(4px)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      )}
      {...props}
    />
  );
});

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentProps<typeof DialogPrimitive.Content> &
    VariantProps<typeof dialogContentVariants> & {
      close?: boolean;
      overlay?: boolean;
    }
>(function DialogContent(
  { className, children, close = true, overlay = true, variant, ...props },
  forwardedRef
) {
  const contentRef =
    React.useRef<React.ElementRef<typeof DialogPrimitive.Content>>(null);
  const mirrorsRef = React.useRef<HTMLElement[]>([]);

  React.useLayoutEffect(() => {
    const contentEl = contentRef.current;
    if (!contentEl) return;

    const rootNode = contentEl.getRootNode();
    if (!(rootNode instanceof ShadowRoot)) return;

    // Radix validates aria-labelledby/describedby via document.getElementById(),
    // which can't see inside Shadow DOM. Mirror those elements to document.body.
    const mirrors: HTMLElement[] = [];

    for (const attr of ["aria-labelledby", "aria-describedby"] as const) {
      const id = contentEl.getAttribute(attr);
      if (!id || document.getElementById(id)) continue;

      const shadowEl = rootNode.querySelector<HTMLElement>(
        `#${CSS.escape(id)}`
      );
      if (!shadowEl) continue;

      const mirror = document.createElement("span");
      mirror.id = id;
      mirror.textContent = shadowEl.textContent ?? "";
      mirror.style.cssText =
        "position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0";
      document.body.appendChild(mirror);
      mirrors.push(mirror);
    }

    mirrorsRef.current = mirrors;

    return () => {
      for (const el of mirrorsRef.current) el.remove();
      mirrorsRef.current = [];
    };
  }, [children]);

  return (
    <DialogPortalWithShadow>
      {overlay && <DialogOverlay />}
      <DialogPrimitive.Content
        ref={composeRefs(forwardedRef, contentRef)}
        data-slot="dialog-content"
        className={cn(dialogContentVariants({ variant }), className)}
        {...props}
      >
        {children}
        {close && (
          <DialogClose className="cursor-pointer outline-0 absolute end-5 top-5 rounded-sm opacity-60 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <X className="size-4" />
            <span className="sr-only">Close</span>
          </DialogClose>
        )}
      </DialogPrimitive.Content>
    </DialogPortalWithShadow>
  );
});

export default DialogContent;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-slot="dialog-header"
    className={cn(
      "flex flex-col space-y-1 text-center sm:text-start mb-5",
      className
    )}
    {...props}
  />
);

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-slot="dialog-footer"
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end pt-5 sm:space-x-2.5",
      className
    )}
    {...props}
  />
);

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        "text-lg font-semibold leading-none tracking-tight",
        className
      )}
      {...props}
    />
  );
}

const DialogBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div data-slot="dialog-body" className={cn("grow", className)} {...props} />
);

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger
};
